<template>
  <section class='crypto-payout-batch-wrapper'>
    <!-- Coming soon page for non whitelisted merchants -->
    <article class="coming-soon" v-if="!isBatchAllowed">
      <h3 class="h4">Import and make multiple payouts</h3>
      <p>Our batch payout feature is currently in beta. If you are interested in using this feature, please contact our <a href="mailto:support@triple-a.io">support team</a>.</p>
    </article>
    <p class="banner warning" v-else-if="!hasPayoutCreationRights">
      You do not have sufficient rights to be able to upload batch payouts.
    </p>
    <!-- Step 1: Presentation & template -->
    <section class="initial" v-else-if="!currentStep || currentStep === 1">
      <div>
        <h3 class="h4">Import and make multiple payouts</h3>
        <p>You can make up to {{ MAX_BATCH_SIZE }} transfers at once in a few simple steps.</p>
        <dl>
          <div>
            <dt><span>1</span>Download the Template & Fill Data</dt>
            <dd>Download the CSV template and enter your payout data (e.g., recipient email, amounts).</dd>
          </div>
          <div>
            <dt><span>2</span>Upload CSV</dt>
            <dd>Once you’ve completed the template, save it as a CSV file and upload it below.</dd>
          </div>
          <div>
            <dt><span>3</span>Check any comments</dt>
            <dd>The system will automatically validate the file. If there are comments, correct them in the CSV and re-upload.</dd>
          </div>
          <div>
            <dt><span>4</span>Review & Confirm</dt>
            <dd>After uploading the file, you’ll be taken to a review page.
              Double-check the payout details, then confirm to finalize the batch payout.
            </dd>
          </div>
        </dl>
      </div>
      <div class="template">
        <h3>Download a template</h3>
        <a href="https://static.triple-a.io/assets/csv_templates/crypto_batch_payout_template.csv">
          Download template
          <svg xmlns="http://www.w3.org/2000/svg" height="24" width="24">
            <path d="M0 0h24v24H0z" fill="none" />
            <path fill="#ffffff" d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
          </svg>
        </a>
      </div>
      <div class="upload-template">
        <!-- upload document -->
        <h3>{{ !isValidatingCsv ? 'Upload your CSV' : 'Validating file' }}</h3>
        <p v-if="uploadedFileName">File uploaded: {{ uploadedFileName }}</p>
        <label for="file" v-if="!isValidatingCsv">
          Select a file
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#FFF">
            <path fill="none" d="M0 0h24v24H0V0z" />
            <path d="M14 2H6c-1.1 0-1.99.9-1.99 2L4 20c0 1.1.89 2 1.99 2H18c1.1 0 2-.9 2-2V8l-6-6zm4 18H6V4h7v5h5v11zM8 15.01l1.41 1.41L11 14.84V19h2v-4.16l1.59 1.59L16 15.01 12.01 11z" />
          </svg>
        </label>
        <input type="file" id="file" @change="handleFileInput" accept=".csv" ref="csvBatchInput" />
        <div class="loader-factory" v-if="isValidatingCsv"></div>
      </div>

      <!-- Errors -->
      <aside v-if="isValidationFinished && !isValidationSuccess" class="validation-errors">
        <h3 class="h4">We found some mistakes</h3>
        <p>
          We found some mistakes in your file. Please review and correct them before retrying. <br /> Need help ? <a href="mailto:support@triple-a.io?subject=Crypto payout Batch upload">Reach out to our support team</a>
        </p>

        <ul class="banner error">
          <li v-if="!isFileFormatValid">
            Invalid file format. Please upload a CSV file.
          </li>
          <li v-if="csvData.length > MAX_BATCH_SIZE + 1">
            The file is too large. Please upload a file with less than {{ MAX_BATCH_SIZE }} payouts.
          </li>
          <li v-else-if="csvData.length < 2">
            The file is empty. Please upload a file with at least one payout.
          </li>
          <li>
            Some errors have been detected. Please review and re-upload the corrected file.
          </li>
          <li v-if="validatedBatch.hasOtherCurrency">
            It looks like the currency you selected for the payouts doesn’t match your merchant account currency. Please ensure you've selected the correct currency and account before continuing.
          </li>
          <li v-if="validatedBatch.isRequestedAmountSuperiorToBalance">
            The total payout amount exceeds your account balance. Please deposit additional funds into your account, or reduce the number of payouts.
          </li>
        </ul>

        <div class="table-wrapper-custom" v-if="csvData.length > 2 && csvData.length < MAX_BATCH_SIZE + 1">
          <div>
            <table>
              <thead>
                <tr>
                  <th>Error</th>
                  <th>Row</th>
                  <th>Currency</th>
                  <th>Amount</th>
                  <th>Recipient email</th>
                  <th>Reference for payout</th>
                  <th>Phone country</th>
                  <th>Phone number</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(txn, index) of errorsInCsv" :key="txn.email + txn.amount + '_' + index">
                  <td>{{ txn.remarks }}</td>
                  <td>{{ txn.csvRowNumber }}</td>
                  <td :class="{ 'error': txn.currency.isError }">{{ txn.currency.val }}</td>
                  <td :class="{ 'error': txn.amount.isError }">{{ txn.amount.val }}</td>
                  <td :class="{ 'error': txn.email.isError }">{{ txn.email.val }}</td>
                  <td :class="{ 'error': txn.externalReference.isError }">{{ txn.externalReference.val }}</td>
                  <td :class="{ 'error': txn.phoneCountryIndicator.isError }">{{ txn.phoneCountryIndicator.val }}</td>
                  <td :class="{ 'error': txn.phoneNumber.isError }">{{ txn.phoneNumber.val }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </aside>
    </section>

    <!-- Step 2: Review and confirm -->
    <section class="review-confirm" v-else-if="currentStep === 2">
      <button class="btn link-style icon-btn back" @click="currentStep--">Back</button>
      <h3 class="h4">Review and confirm</h3>
      <p>Here is the list of payouts dedetected in the submitted file. Please review and confirm.
        <br />If you wish to modify, <button class="btn link-style" @click="currentStep--">head back</button> and re-upload a modified CSV.
      </p>
      <ul class="banner error" v-if="submissionErrors.length > 0">
        <li>
          Submission failed. Please review and retry. If errors persist please <a href="mailto:support@triple-a.io">contact our support team</a>.
        </li>
        <li v-for="(e, index) in submissionErrors" :key="`submission_error${e.code}_${index}`">
          {{ e.message }} ({{ e.code }})
        </li>
      </ul>
      <article :is-loading="isBackendRequestLoading">
        <dl>
          <div>
            <dt>File submitted :</dt>
            <dd>{{ uploadedFileName }}</dd>
          </div>
          <div>
            <dt>Number of payouts :</dt>
            <dd>{{ validatedBatch.payouts.length }}</dd>
          </div>
          <div>
            <dt>Unique recipients :</dt>
            <dd>{{ validatedBatch.uniqueRecipients.size }}</dd>
          </div>
          <div>
            <dt>Total amount :</dt>
            <dd>{{ formatFiatPrice(validatedBatch.totalAmount) }} {{validatedBatch.currency}}</dd>
          </div>
        </dl>
        <div class="action-group">
          <button class="btn confirm" @click="submitBatch()">
            {{ isBackendRequestLoading ? 'Submitting...' : 'Confirm' }}
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#FFF">
              <path fill="none" d="M0 0h24v24H0z" />
              <path d="M2.01 21 23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
          </button>
          <button class="btn edit" @click="currentStep--">
            Back and edit
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="#FFF">
              <path fill="none" d="M0 0h24v24H0z" />
              <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a.996.996 0 0 0 0-1.41l-2.34-2.34a.996.996 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
            </svg>
          </button>
        </div>
      </article>
      <div class="table-wrapper-custom">
        <div>
          <table>
            <thead>
              <tr>
                <th>Currency</th>
                <th>Amount</th>
                <th>Recipient email</th>
                <th>Reference for payout (optional)</th>
                <th>Phone number (optional)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(pyt, index) in validatedBatch.payouts" :key="`pyt_${index}_${pyt.withdraw_amount}`">
                <td>{{ pyt.withdraw_currency }}</td>
                <td>{{ formatFiatPrice(pyt.withdraw_amount) }}</td>
                <td>{{ pyt.email }}</td>
                <td>{{ pyt.order_id }}</td>
                <td>{{ pyt.mobile_number }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- Success screen -->
    <section class="success-screen" v-else-if="hasBackendAccepted && currentStep === 3">
      <img src="../assets/icons/checkedTask_green.svg" alt="success" />
      <h3 class="h2">
        Submission successful
      </h3>
      <p>You will receive an email when the batch has been fully processed or if an action is needed.
        <br /> Need help ? <a href="#">Reach out to our support team</a>
      </p>

      <ul>
        <li class="done">
          <span>1</span>
          <p>Creating batch<br />
            <small>Preparing payouts</small>
          </p>
        </li>
        <li class="step-left">
          <span>2</span>
          <p>
            Batch processing <br />
            <small>Creating payouts</small>
          </p>
        </li>
        <li class="step-left">
          <span>3</span>
          <p>
            Batch completed<br />
            <small>All emails have been sent to recipients</small>
          </p>
        </li>
      </ul>
      <dl class="batch-recap">
        <div>
          <dt>Status</dt>
          <dd>
            <tag>{{ submittedBatch.status }}</tag>
          </dd>
        </div>
        <div>
          <dt>Created at</dt>
          <dd>{{ formatDate(submittedBatch.created_at, 'DD-MMM-YY') }}</dd>
        </div>
        <div>
          <dt>Notification email</dt>
          <dd>{{ submittedBatch.notification_email }}</dd>
        </div>
        <div class="breakable-string">
          <dt>Batch name</dt>
          <dd>{{ submittedBatch.name }}</dd>
        </div>
        <div class="breakable-string">
          <dt>ID</dt>
          <dd>{{ submittedBatch.id }}</dd>
        </div>
      </dl>
      <div class="action-group">
        <router-link to="/crypto-payout-admin" class="btn style-link">View all payouts history</router-link>
        <router-link to="/crypto-payout-admin/batch/" class="btn style-link">View batch history</router-link>
      </div>
    </section>
  </section>
</template>
<script>
import Tag from '../atoms/Tag.vue';

export default {
  components: { Tag },
  data() {
    return {
      MAX_BATCH_SIZE: 500,
      ROW_INDEXES: {
        currency: 0,
        amount: 1,
        email: 2,
        orderId: 3,
        phoneCountryIndicator: 4,
        phoneNumber: 5,
      },
      hasPayoutCreationRights: false,
      csvData: [],
      currentStep: 1,
      validatedBatch: {
        payouts: [],
        uniqueRecipients: new Set(),
        totalAmounts: [],
      },
      submittedBatch: {},
      errorsInCsv: [],
      uploadedFileName: null,

      submissionErrors: [],
      isLoadingMore: false,
      isValidationFinished: false,
      hasBackendAccepted: false,
      isBackendRequestLoading: false,
      isValidatingCsv: false,
      isValidationSuccess: true,
      isFileFormatValid: true,
    };
  },
  mounted() {
    this.checkPayoutRights();
  },
  methods: {
    checkPayoutRights() {
      const activeMerchant = this.$store.getters.activeMerchantGetter;
      this.hasPayoutCreationRights = activeMerchant && this.isAllowed(
        this.$store.getters.currentActiveEntityAdminGetter,
        activeMerchant.id,
        'payout_links',
        'W'
      );
    },

    submitBatch() {
      this.isBackendRequestLoading = true;
      const email = this.$store.getters.merchantProfileDetailsGetter.merchant_email;
      const mkey = this.$store.getters.activeMerchantGetter.merchant_key;
      const payload = {
        merchantKey: mkey,
        batchName: this.uploadedFileName,
        email: email,
        payouts: this.validatedBatch.payouts,
      };
      this.submissionErrors = [];
      this.$recaptchaLoaded().then(() => {
        this.$recaptcha('browser_access').then((token) => {
          payload.recaptchaToken = token;
          this.$store.dispatch('createBatchCryptoPayout', payload).then((response) => {
            this.submittedBatch = response.data;
            this.isBackendRequestLoading = false;
            this.hasBackendAccepted = true;
            this.currentStep++;
          }).catch((error) => {
            this.isBackendRequestLoading = false;
            this.hasBackendAccepted = false;
            this.handleSubmitError(error.response.data);
          });
        });
      }).catch(() => {
        this.isBackendRequestLoading = false;
        this.submissionErrors.push({ message: 'Error while getting recaptcha token', code: 500 });
      });
    },

    handleFileInput(event) {
      this.isValidationFinished = false;
      this.isValidationSuccess = true;
      this.isFileFormatValid = true;
      this.resetValues();
      const file = event.target.files[0];
      if (!file) {
        return;
      }
      // check file format
      const fileExtension = file.name.split('.').pop();
      const allowedFormat = ['csv'];
      if (!allowedFormat.includes(fileExtension)) {
        this.isValidationFinished = true;
        this.isValidationSuccess = false;
        this.isFileFormatValid = false;
        return;
      }
      // save file name without extension
      this.uploadedFileName = file.name;
      // read file
      const reader = new FileReader();
      reader.onload = (e) => {
        this.csvData = e.target.result.split('\n').map((row) => row.split(','));
        if (this.validateCsv(this.csvData)) {
          this.isValidationFinished = true;
          this.isValidationSuccess = true;
          this.currentStep++;
        } else {
          this.isValidationFinished = true;
          this.isValidationSuccess = false;
        }
        this.$refs.csvBatchInput.value = '';
      };
      reader.onerror = (e) => {
        console.error('Error while reading file', e);
        this.isValidationFinished = true;
        this.isValidationSuccess = false;
        this.isValidatingCsv = false;
      };
      reader.readAsText(file);
    },

    validateCsv(csvData) {
      // Min length is 2 (header + 1 row), max length is 500 + 1 header
      if (csvData.length < 2 || csvData.length > this.MAX_BATCH_SIZE + 1) {
        return false;
      }
      this.isValidatingCsv = true;
      this.resetValidatedBatch();
      this.errorsInCsv = [];
      for (let row = 1; row < csvData.length; row++) {
        const cleanRow = this.cleanCells(csvData[row]);
        if (this.isRowEmpty(cleanRow)) {
          continue;
        }
        // check if row has all required data well formatted
        const [isRowValid, rowErrors] = this.validateRow(cleanRow);
        if (!isRowValid) {
          // handle error an continue
          this.addError(csvData[row], rowErrors, row);
        } else {
          const pyt = this.formatPayoutFromRow(cleanRow);
          this.addBatchData(pyt);
          this.validatedBatch.payouts.push(pyt);
        }
      }

      if (this.isTotalAmountTooBig()) {
        this.validatedBatch.isRequestedAmountSuperiorToBalance = true;
        return false;
      }

      this.isValidatingCsv = false;
      if (this.errorsInCsv.length) {
        return false;
      }

      return true;
    },

    validateRow(row) {
      // check if row has all required data well formatted
      if (!row || row.length < 4) {
        return [
          false,
          {
            currency: true,
            amount: true,
            email: true,
            orderId: true,
            phoneNumber: true,
          }
        ];
      }
      // following the csv template order
      const currency = row[this.ROW_INDEXES.currency];
      const amount = row[this.ROW_INDEXES.amount];
      const email = row[this.ROW_INDEXES.email];
      const orderId = row[this.ROW_INDEXES.orderId];
      const phoneCountryIndicator = row[this.ROW_INDEXES.phoneCountryIndicator];
      const phoneNumber = row[this.ROW_INDEXES.phoneNumber];
      const errors = {
        currency: '',
        amount: '',
        email: '',
        orderId: '',
        phoneNumber: '',
      };
      if (!this.isCurrencyValid(currency)) {
        errors.currency = true;
      }
      if (!this.isAmountValid(amount)) {
        errors.amount = true;
      }
      if (!this.isEmailValid(email)) {
        errors.email = true;
      }
      if (!this.isOrderIdValid(orderId)) {
        errors.orderId = true;
      }
      if (!this.isPhoneNumberValid(phoneCountryIndicator, phoneNumber)) {
        errors.phoneNumber = true;
      }

      const isValid = !errors.currency && !errors.amount && !errors.email && !errors.orderId && !errors.phoneNumber;
      return [isValid, errors];
    },

    addError(row, errors, csvRowNumber) {
      const currency = row[this.ROW_INDEXES.currency];
      const amount = row[this.ROW_INDEXES.amount];
      const email = row[this.ROW_INDEXES.email];
      const orderId = row[this.ROW_INDEXES.orderId];
      const phoneCountryIndicator = row[this.ROW_INDEXES.phoneCountryIndicator];
      const phoneNumber = row[this.ROW_INDEXES.phoneNumber];
      this.errorsInCsv.push({
        currency: { val: currency, isError: errors.currency },
        amount: { val: amount, isError: errors.amount },
        email: { val: email, isError: errors.email },
        externalReference: { val: orderId, isError: errors.orderId },
        phoneCountryIndicator: { val: phoneCountryIndicator, isError: errors.phoneNumber },
        phoneNumber: { val: phoneNumber, isError: errors.phoneNumber },
        remarks: this.getErrorMsg(errors),
        csvRowNumber,
      });
    },

    addBatchData(pyt) {
      this.validatedBatch.uniqueRecipients.add(pyt.email);
      // extra check just in case
      if (this.validatedBatch.currency === pyt.withdraw_currency) {
        this.validatedBatch.totalAmount += pyt.withdraw_amount;
      }
    },

    getErrorMsg(errors) {
      const errorMessages = [];
      if (errors.currency) {
        errorMessages.push('currency');
      }
      if (errors.amount) {
        errorMessages.push('amount');
      }
      if (errors.email) {
        errorMessages.push('email');
      }
      if (errors.orderId) {
        errorMessages.push('reference');
      }
      if (errors.phoneNumber) {
        errorMessages.push('mobile number');
      }
      return 'Invalid ' + errorMessages.join(', ');
    },

    /**
     * Clean cells from unwanted characters like \r or \t
     * Also removing leading and trailing spaces, 
     * and + or = so we can add it later without having to check any issue
     */
    cleanCells(data) {
      const cleanedData = data.map((cell) => cell.replace(/[\r\t]/g, '').trim());
      return cleanedData;
    },

    /********************************** VALDATION FUNCTIONS **********************************/
    isCurrencyValid(currency) {
      // check if currency is valid
      if (!currency || currency.length !== 3) { 
        return false;
      }
      const isSameAsBalanceCurrency = currency === this.$store.getters.activeMerchantGetter.local_currency;
      // extra step to display a better error mesage for users
      if (!isSameAsBalanceCurrency) {
        this.validatedBatch.hasOtherCurrency = true;
      }
      // needs to be 3 character long
      return isSameAsBalanceCurrency;
    },

    isAmountValid(amount) {
      if (!amount) {
        return false;
      }
      if (typeof amount === 'string') {
        amount = amount.replace(/[, ]+/g, '');
      }
      // Regular expression to match a valid number with up to 2 decimal places
      const regex = /^([1-9]\d*|0)?(\.\d{1,2})?$/;
      if (regex.test(amount)) {
        return parseFloat(amount) > 0.01;
      }
      return false;
    },

    isEmailValid(email) {
      if (!email) {
        return false;
      }
      // check if email is valid
      return this.isEmail(email);
    },

    isPhoneNumberValid(countryIndicator, phoneNumber) {
      // check if both are present or both are missing
      if (phoneNumber && !countryIndicator
        || !phoneNumber && countryIndicator) {
        return false;
      } else if (!phoneNumber && !countryIndicator) {
        return true;
      }
      // 1-20 digits, accepts + and = at the beginning since excel might add it if user inputs '+'
      const mobileRegex = /^=?\+?[1-9]\d{1,20}$/;
      const mobileNumber = `${countryIndicator}${phoneNumber}`.replace(/[ ]+/g, '');
      return mobileRegex.test(mobileNumber);
    },

    isOrderIdValid(orderId) {
      // check if order id is valid
      return !orderId || orderId.length < 50;
    },

    /*
      * check if total amount if superior to thew merchant balance
      * this is a roiugh check. It is possible that the final total amount is superior to the balance
      * especially if the fees are high
      */
    isTotalAmountTooBig() {
      const merchantBalance = this.$store.getters.activeMerchantGetter.balances.find((b) => (b.available_currency === this.validatedBatch.currency && b.type === 'real'));
      if (this.validatedBatch.currency === this.$store.getters.activeMerchantGetter.local_currency
        && this.validatedBatch.totalAmount >= merchantBalance.available_amount) {
        this.validatedBatch.isRequestedAmountSuperiorToBalance = true;
        return true;
      }
      return false;
    },

    isRowEmpty(row) {
      return row.every((cell) => !cell);
    },

    resetValues() {
      this.hasBackendAccepted = false;
      this.isValidationFinished = false;
      this.isLoadingMore = false;
      this.isBackendRequestLoading = false;
      this.resetValidatedBatch();
      this.submissionErrors = [];
    },

    resetValidatedBatch() {
      this.validatedBatch = {
        payouts: [],
        uniqueRecipients: new Set(),
        totalAmount: 0,
        currency: this.$store.getters.activeMerchantGetter.local_currency,
        isRequestedAmountSuperiorToBalance: false,
        hasOtherCurrency: false,
      };
    },

    /********************************** FORMATTING FUNCTIONS **********************************/
    formatPayoutFromRow(row = []) {
      // following the csv template order
      try {
        const pyt = {
          withdraw_currency: row[this.ROW_INDEXES.currency],
          withdraw_amount: parseFloat(row[this.ROW_INDEXES.amount]),
          email: row[this.ROW_INDEXES.email],
        };
        if (row[this.ROW_INDEXES.orderId]) {
          pyt.order_id = row[this.ROW_INDEXES.orderId];
        }
        // clean the country indicator and combine the 2 phone number fields
        if (row[this.ROW_INDEXES.phoneCountryIndicator] && row[this.ROW_INDEXES.phoneNumber]) {
          const indicator = row[this.ROW_INDEXES.phoneCountryIndicator] && row[this.ROW_INDEXES.phoneCountryIndicator].startsWith('+') ? row[this.ROW_INDEXES.phoneCountryIndicator] : `+${row[this.ROW_INDEXES.phoneCountryIndicator]}`;
          pyt.mobile_number = `${indicator}${row[this.ROW_INDEXES.phoneNumber]}`;
        }
        return pyt;
      } catch (e) {
        console.error('Error while formatting payout from row', e);
        return {};
      }
    },

    listTotalAmounts(batch) {
      const totalAmounts = [];
      for (const [currency, amount] of batch.totalAmounts) {
        totalAmounts.push(`${this.formatFiatPrice(amount)} ${currency}`);
      }
      return totalAmounts.join(', ');
    },

    handleSubmitError(error) {
      const e = {
        message: error.message,
        code: error.statusCode,
      };
      if (error.statusCode === 409) {
        e.message = 'This batch was already submitted once. Please submit a file with a different name or different data.';
        this.submissionErrors.push(e);
      }
      else if (error.statusCode === 400) {
        for (const [code, msg] of Object.entries(error.errors)) {
          this.submissionErrors.push({ message: msg, code });
        }
      }
      else {
        this.submissionErrors.push(e);
      }
    },



  },
  watch: {
    activeMerchantGetter: function () {
      this.checkPayoutRights();
    },
  },
  computed: {
    isBatchAllowed() {
      return this.isWhitelistedForCryptoBatch();
    },
    activeMerchantGetter() {
      return this.$store.getters.activeMerchantGetter;
    },
  },
};
</script>
<style lang="scss">
.crypto-payout-batch-wrapper {
  max-width: 1200px;
  margin-top: $margin-l;

  section.initial {
    display: flex;
    flex-wrap: wrap;
    gap: $margin-l;
    max-width: 900px;

    // Presentation & template
    &>div:first-of-type {
      width: 100%;
      max-width: 900px;
      border: grey 1px solid;
      border-radius: 12px;
      padding: $margin-l;
      margin-top: $margin-m;

      dl {
        display: flex;
        flex-wrap: wrap;
        gap: $margin-l;
        margin-top: $margin-m;
        margin-bottom: $margin-m;

        &>div {
          margin-top: $margin-m;
          min-width: 250px;
          max-width: 400px;

          dt {
            font-weight: bold;

            span {
              display: inline-block;
              margin-right: $margin-s;
              border-radius: 12px;
              width: 24px;
              height: 24px;
              background-color: $color-primary;
              color: white;
              text-align: center;
              padding-top: 2px;
            }
          }
        }
      }
    }

    // Upload template
    .upload-template {
      height: 300px;
      // half minus gap + padding
      width: calc(50% - 24px + 12px);
      display: flex;
      background: $color-selectable;
      border-radius: 12px;
      align-items: center;
      justify-content: center;
      flex-direction: column;

      h3 {
        margin-bottom: $margin-m;
      }

      p {
        max-width: 300px;
        text-align: center;
        word-break: break-all;
        white-space: wrap;
        margin-bottom: $margin-m;
      }

      label {
        background-color: $color-primary;
        color: white;
        padding: 6px 12px;
        border-radius: 24px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;

        &:hover {
          background-color: darken($color-primary, 10%);
        }
      }

      input {
        display: none;
      }

      &[is-loading] {
        label {
          background-color: darken($color-selectable, 20%);
          cursor: not-allowed;

          &:hover {
            background-color: darken($color-selectable, 20%);
          }
        }

      }
    }

    .template {
      display: flex;
      flex-flow: column;
      align-items: center;
      justify-content: center;
      min-width: 200px;
      max-width: 500px;
      // half minus gap + padding
      width: calc(50% - 24px + 12px);
      border: grey 1px solid;
      border-radius: 12px;
      padding: $margin-xl;
      text-align: center;

      h3 {
        margin-bottom: $margin-m;
      }

      a {
        border-radius: 24px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        background: $color-primary;
        padding: 6px 12px;

        &:hover {
          background: darken($color-primary, 10%);
        }
      }
    }

    aside.validation-errors {
      margin-top: $margin-m;
      width: 100%;

      .banner {
        margin-top: $margin-s;
        margin-bottom: $margin-s;
        li {
          margin-bottom: $margin-s;
        }
      }
    }
  }

  section.review-confirm {
    margin-top: $margin-m;

    h3 {
      margin-bottom: $margin-m;
    }

    &>button.link-style {
      margin-bottom: $margin-m;
      color: $color-text;
    }

    &>p:first-of-type {
      margin-bottom: $margin-s;

      button {
        margin: 0;
        padding-left: 0;
        padding-right: 0;
      }
    }

    article {
      display: flex;
      flex-wrap: wrap;
      gap: $margin-l;
      align-items: center;
      justify-content: space-between;
      // flex-flow: column;
      padding: $margin-m $margin-l;
      border: none;
      border-radius: 12px;
      box-shadow: 0 0 8px rgba(0, 0, 0, 0.1);
      max-width: 100%;
      margin-bottom: $margin-m;

      background: white;

      dl {
        margin-bottom: $margin-s;

        &>div {
          margin-top: $margin-s;
          min-width: 250px;
          max-width: 600px;
          display: flex;

          dt {
            margin-right: $margin-s;
            white-space: nowrap;
            font-weight: bold;
          }
        }
      }

      div.action-group {
        display: flex;
        gap: $margin-m;
        flex-flow: column;

        button {
          color: white;
          border: none;
          padding: 12px 24px;
          cursor: pointer;
          width: 200px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 0;

          svg {
            margin-left: $margin-m;
          }

          &.confirm {
            background: $color-success;

            &:hover {
              background: darken($color-success, 10%);
            }
          }

          &.edit {
            background: $color-primary;
            color: white;
            height: 48px;

            &:hover {
              background: darken($color-primary, 10%);
            }
          }
        }
      }

      &[is-loading] {
        .action-group {
          button.send {
            background: darken($color-selectable, 20%);
            cursor: not-allowed;

            svg {
              display: none;
            }

            &:hover {
              background: darken($color-selectable, 20%);
            }
          }
        }
      }
    }
  }

  section.success-screen {
    display: flex;
    flex-direction: column;
    gap: $margin-l;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 8px rgba(0, 0, 0, 0.1);
    padding: 40px;
    border-radius: 12px;
    max-width: max-content;

    &>img {
      width: 60px;
      height: 60px;
    }

    dl {
      display: flex;
      flex-wrap: wrap;
      gap: $margin-l;
      background: $color-selectable;
      padding: $margin-l;
      border-radius: 6px;

      div {
        margin-top: $margin-s;
        max-width: 400px;

        dt {
          font-weight: bold;
        }

        &.breakable-string {
          max-width: 200px;

          dd {
            word-break: break-all;
            white-space: wrap;
          }
        }
      }
    }

    ul {
      display: flex;
      flex-direction: column;
      gap: 24px;
      max-width: 800px;

      li {
        display: flex;
        gap: $margin-s;
        align-items: flex-start;
        position: relative;

        span {
          display: inline-block;
          width: 24px;
          height: 24px;
          border-radius: 12px;
          text-align: center;
          padding-top: 2px;
          flex-shrink: 0;
          background: $color-selectable;
          color: $color-text;
        }

        &>p {
          display: inline;
          max-width: 550px;
        }

        &::after {
          content: '';
          display: block;
          width: 1px;
          margin-left: 12px;
          position: absolute;
          height: calc(100% - 6px - 6px);
          top: 30px;
          border-left: 1px dashed $color-text;
        }

        &.done {
          span {
            background: $color-success;
            color: white;
          }

          &::after {
            border-left: 1px dashed $color-success;
          }
        }

        &.current-step {
          span {
            background: white;
            color: $color-success;
            border: 1px solid $color-success;

          }

          &::after {
            border-left: 1px dashed $color-text;
          }
        }

        &:last-child {
          &::after {
            display: none;
          }
        }
      }
    }

    &>p {
      font-size: 14px;
      color: $color-text;
      text-align: center;

      a:hover {
        text-decoration: underline;
      }
    }

    div.action-group {
      display: flex;
      gap: $margin-m;

      button,
      a {
        padding: 12px 24px;
        background: $color-primary;
        color: white;
        border: none;
        border-radius: 6px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 230px;

        &:hover {
          background: darken($color-primary, 10%);
        }
      }
    }
  }

  article.coming-soon {
    padding: 40px;
    border-radius: 12px;
    border: 1px solid $color-selectable;

    h3 {
      margin-bottom: $margin-m;
    }
  }

  /* HTML: <div class="loader"></div> */
  .loader-factory {
    width: 90px;
    height: 14px;
    background: linear-gradient(#fff 0 0) 50%/2px 100% no-repeat;
    display: grid;
  }

  .loader-factory:before,
  .loader-factory:after {
    content: "";
    grid-area: 1/1;
    background: radial-gradient(circle closest-side, var(--c, #161141) 92%, #0000) 0 0/calc(100%/4) 100%;
    clip-path: inset(0 50% 0 0);
    animation: l3 1s infinite linear;
  }

  .loader-factory:after {
    --c: #00ff88;
    clip-path: inset(0 0 0 50%);
  }

  @keyframes l3 {
    100% {
      background-position: calc(100%/3) 0
    }
  }
}
</style>